/**
 * DashboardTeam — Manager's team KPI view.
 *
 * Shows each direct report with their KPI summary and assignments.
 * Includes AI-generated team narrative at the top.
 */
import React, { useEffect, useState } from 'react';
import Users from 'lucide-react/dist/esm/icons/users';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import { Spinner } from '../../../components/ui';
import { useKPIStore } from '../../../stores/kpiStore';
import KPIAssignmentCard from './KPIAssignmentCard';
import * as kpiApi from '../../../services/api/kpi';

interface DashboardTeamProps {
  onSelectAssignment: (id: string) => void;
}

const DashboardTeam: React.FC<DashboardTeamProps> = ({ onSelectAssignment }) => {
  const {
    teamDashboard, teamDashboardLoading, fetchTeamDashboard, activeCycleId,
  } = useKPIStore();

  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [aiNarrative, setAiNarrative] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    fetchTeamDashboard();
  }, [activeCycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMember = (userId: string) => {
    setExpandedMembers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const loadAISummary = async () => {
    setAiLoading(true);
    try {
      const resp = await kpiApi.aiTeamSummary(activeCycleId || undefined);
      setAiNarrative(resp.summary);
    } catch {
      setAiNarrative('Unable to generate summary at this time.');
    }
    setAiLoading(false);
  };

  if (teamDashboardLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner variant="success" />
      </div>
    );
  }

  if (!teamDashboard || teamDashboard.members.length === 0) {
    return (
      <div className="text-center py-16 max-w-lg mx-auto animate-fade-in">
        <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-surface-secondary border border-default flex items-center justify-center">
          <Users size={28} className="text-content-muted" />
        </div>
        <h3 className="text-base font-semibold text-content-secondary mb-2">No Direct Reports</h3>
        <p className="text-sm text-content-tertiary mb-4 leading-relaxed">
          This tab shows KPI progress for people who report to you. Currently, no one in the organization
          has their <strong className="text-content-secondary">"Reports To"</strong> field set to you.
        </p>
        <div className="p-4 rounded-xl bg-surface-primary border border-subtle text-left max-w-sm mx-auto">
          <p className="text-xs text-content-tertiary mb-2 font-medium">To set up your team:</p>
          <ol className="text-xs text-content-tertiary space-y-1.5 list-decimal list-inside">
            <li>Go to the <strong className="text-content-secondary">Organization</strong> module (gear icon, sidebar)</li>
            <li>Open a team member's profile in the <strong className="text-content-secondary">People</strong> tab</li>
            <li>Set their <strong className="text-content-secondary">"Reports To"</strong> field to yourself</li>
          </ol>
        </div>
      </div>
    );
  }

  const { members, team_summary } = teamDashboard;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Team Summary Bar */}
      <div className="flex items-center gap-6 p-4 rounded-xl bg-surface-primary border border-subtle">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-primary" />
          <span className="text-sm font-medium text-content">{members.length} team member{members.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-emerald-400">{team_summary.on_track} on track</span>
          <span className="text-amber-400">{team_summary.at_risk} at risk</span>
          <span className="text-status-error">{team_summary.behind} behind</span>
        </div>
          <button
            onClick={loadAISummary}
            disabled={aiLoading}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-purple-400 bg-purple-400/10 hover:bg-purple-400/15 transition-colors cursor-pointer border border-purple-400/15 outline-none disabled:opacity-50"
          >
          <Sparkles size={10} />
          {aiLoading ? 'Generating...' : 'AI Summary'}
        </button>
      </div>

      {/* AI Narrative */}
      {aiNarrative && (
        <div className="p-4 rounded-xl bg-purple-400/[0.04] border border-purple-400/15 animate-fade-in">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-purple-400" />
            <span className="text-xs font-medium text-purple-400">AI Team Summary</span>
          </div>
          <div className="text-sm text-content-secondary leading-relaxed whitespace-pre-wrap">{aiNarrative}</div>
        </div>
      )}

      {/* Team Members */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        {members.map((member) => {
          const isExpanded = expandedMembers.has(member.user_id);
          const score = member.overall_score;

          return (
            <div key={member.user_id} className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
              {/* Member Header */}
              <button
                type="button"
                onClick={() => toggleMember(member.user_id)}
                className="w-full flex items-center gap-4 p-4 text-left cursor-pointer outline-none hover:bg-surface-primary transition-colors"
              >
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand/25 to-status-success/25 flex items-center justify-center text-xs font-bold text-content-inverse shrink-0">
                  {(member.display_name || member.email)[0]?.toUpperCase()}
                </div>

                {/* Name + summary */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-content truncate">
                      {member.display_name || member.email.split('@')[0]}
                    </span>
                    {member.title && (
                      <span className="text-[11px] text-content-muted truncate">{member.title}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px]">
                    <span className="text-emerald-400/80">{member.summary.on_track} on track</span>
                    {member.summary.at_risk > 0 && (
                      <span className="text-amber-400/80">{member.summary.at_risk} at risk</span>
                    )}
                    {member.summary.behind > 0 && (
                      <span className="text-status-error/80">{member.summary.behind} behind</span>
                    )}
                  </div>
                </div>

                {/* Score */}
                {score !== null && (
                  <span className={`text-base font-bold tabular-nums ${
                    score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-status-error'
                  }`}>
                    {Math.round(score)}%
                  </span>
                )}

                {/* Expand toggle */}
                {isExpanded
                  ? <ChevronDown size={14} className="text-content-muted shrink-0" />
                  : <ChevronRight size={14} className="text-content-muted shrink-0" />
                }
              </button>

              {/* Expanded: assignment cards */}
              {isExpanded && (
                <div className="px-4 pb-4 space-y-2 animate-fade-in">
                  {member.assignments.length > 0 ? (
                    member.assignments.map((a) => (
                      <KPIAssignmentCard
                        key={a.id}
                        assignment={a}
                        onClick={onSelectAssignment}
                      />
                    ))
                  ) : (
                    <p className="text-xs text-content-muted py-4 text-center">No KPIs assigned</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(DashboardTeam);
